import React, { useEffect, useRef, useState } from "react";
import "../../../common/chstFilterControls.css";
import "./weekSelect.css";

export interface WeekOption {
  id: number;
  name: string;
  exactDates?: { start?: Date; end?: Date };
}

export function formatCompactWeekLabel(week: WeekOption): string {
  const start = week.exactDates?.start
    ? new Date(week.exactDates.start)
    : null;
  const end = week.exactDates?.end ? new Date(week.exactDates.end) : null;
  if (
    !start ||
    !end ||
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime())
  ) {
    return week.name;
  }

  const startDay = start.getDate();
  const endDay = end.getDate();
  const year = end.getFullYear();

  if (start.getMonth() === end.getMonth()) {
    const month = end.toLocaleString("es-ES", { month: "long" });
    return `${startDay} → ${endDay} ${month} ${year}`;
  }

  const startMonth = start.toLocaleString("es-ES", { month: "short" });
  const endMonth = end.toLocaleString("es-ES", { month: "long" });
  return `${startDay} ${startMonth} → ${endDay} ${endMonth} ${year}`;
}

function getWeekContextLabel(weekId: number): string | null {
  if (weekId === 0) return "Semana anterior";
  if (weekId === 1) return "Semana actual";
  if (weekId === 2) return "Próxima semana";
  return null;
}

function getWeekContextBadgeClass(weekId: number): string {
  if (weekId === 0) return "chst-week-select__option-badge--past";
  if (weekId === 1) return "chst-week-select__option-badge--current";
  if (weekId === 2) return "chst-week-select__option-badge--next";
  return "";
}

interface WeekSelectProps {
  weeks: WeekOption[];
  value: number;
  onChange: (weekId: number) => void;
  disabled?: boolean;
}

const WeekSelect: React.FC<WeekSelectProps> = ({
  weeks,
  value,
  onChange,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedWeek = weeks.find((w) => w.id === value);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleSelect = (weekId: number) => {
    onChange(weekId);
    setIsOpen(false);
  };

  return (
    <div className="chst-filter-control chst-week-select" ref={rootRef}>
      <button
        type="button"
        className={`chst-filter-trigger${isOpen ? " chst-filter-trigger--open" : ""}`}
        onClick={() => !disabled && setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="chst-filter-icon chst-filter-icon--week" aria-hidden="true">
          <i className="far fa-calendar-alt" />
        </span>
        <span className="chst-filter-body">
          <span className="chst-filter-label">Semana</span>
          <span className="chst-filter-value">
            {selectedWeek ? formatCompactWeekLabel(selectedWeek) : "Seleccionar semana"}
          </span>
        </span>
        <span
          className={`chst-filter-chevron${isOpen ? " chst-filter-chevron--open" : ""}`}
          aria-hidden="true"
        >
          <i className="fas fa-chevron-down" />
        </span>
      </button>

      {isOpen && !disabled ? (
        <div className="chst-filter-menu chst-week-select__menu" role="listbox">
          {weeks.map((week) => {
            const isSelected = week.id === value;
            const contextLabel = getWeekContextLabel(week.id);
            return (
              <button
                key={week.id}
                type="button"
                role="option"
                aria-selected={isSelected}
                className={`chst-week-select__option${
                  isSelected ? " chst-week-select__option--selected" : ""
                }`}
                onClick={() => handleSelect(week.id)}
              >
                <span className="chst-week-select__option-main">
                  <span className="chst-week-select__option-label">
                    {formatCompactWeekLabel(week)}
                  </span>
                  {contextLabel ? (
                    <span
                      className={`chst-week-select__option-badge ${getWeekContextBadgeClass(week.id)}`}
                    >
                      {contextLabel}
                    </span>
                  ) : null}
                </span>
                {isSelected ? (
                  <span className="chst-week-select__option-check" aria-hidden="true">
                    <i className="fas fa-check" />
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default WeekSelect;
